import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ANIMALS } from "@/data/animals";
import { MapPinLine, Feather, Eye } from "@phosphor-icons/react";
import { Editable } from "@/components/Editable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpotterLog() {
  const [sightings, setSightings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    observer: "",
    species: ANIMALS[0].name,
    location: "",
    notes: "",
  });

  const loadSightings = async () => {
    try {
      const res = await axios.get(`${API}/sightings`);
      setSightings(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Could not load the logbook just yet.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSightings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.observer.trim() || !form.location.trim()) {
      toast.error("Observer name and location are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/sightings`, form);
      setSightings((prev) => [res.data, ...prev]);
      setForm({ observer: "", species: ANIMALS[0].name, location: "", notes: "" });
      toast.success("Sighting added to the logbook.");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't add your sighting. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="spotted-log-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-20 md:pt-28">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="font-mono-label text-[11px] text-[#5C6B5E]">
              § The Spotter Log · Vol. I
            </div>
            <h1 className="font-serif-display text-6xl md:text-7xl text-[#2A3A2C] mt-3 leading-[0.95]">
              <Editable id="spotterlog.heading" multiline defaultValue={"A shared logbook of sightings."} />
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#5C6B5E] leading-relaxed">
              <Editable
                id="spotterlog.blurb"
                multiline
                defaultValue={"Write what you saw — where, when, and the small particulars. Anonymous entries welcome, but a first name and a place go a long way. Be honest, be observant."}
              />
            </p>
          </div>
        </div>
        <div className="ink-rule mt-10" />
      </section>

      {/* Two columns: form + entries */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 mt-12 grid md:grid-cols-12 gap-10 md:gap-16">
        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className="md:col-span-5 md:sticky md:top-24 self-start bg-[#FAF8F5] border border-[#D1C9B8] p-8 deckled-edge"
          data-testid="sighting-form"
        >
          <div className="flex items-center gap-2 text-[#2A3A2C]">
            <Feather size={18} weight="duotone" />
            <span className="font-mono-label text-[10px]">New Entry</span>
          </div>
          <h2 className="font-serif-display text-3xl text-[#2A3A2C] mt-3">
            Sign the logbook.
          </h2>

          <div className="mt-8 space-y-7">
            <Field
              label="Observer"
              testId="input-observer"
              value={form.observer}
              onChange={(v) => setForm({ ...form, observer: v })}
              placeholder="Your name or initials"
            />
            <SelectField
              label="Species"
              testId="input-species"
              value={form.species}
              onChange={(v) => setForm({ ...form, species: v })}
              options={[...ANIMALS.map((a) => a.name), "Other / Unknown"]}
            />
            <Field
              label="Location"
              testId="input-location"
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              placeholder="e.g. El Porvenir Campground, dawn"
            />
            <TextAreaField
              label="Notes"
              testId="input-notes"
              value={form.notes}
              onChange={(v) => setForm({ ...form, notes: v })}
              placeholder="Behavior, weather, any details worth remembering…"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-sighting-btn"
            className="mt-9 w-full inline-flex items-center justify-center gap-2 bg-[#B75D3F] text-white px-7 py-4 font-mono-label text-[11px] hover:bg-[#964B32] transition-colors disabled:opacity-60"
          >
            <Eye size={16} />
            {submitting ? "Adding…" : "Add Sighting"}
          </button>
        </form>

        {/* ENTRIES */}
        <div className="md:col-span-7" data-testid="sightings-list">
          <div className="flex items-baseline justify-between">
            <h3 className="font-serif-display text-3xl text-[#2A3A2C]">
              Recent entries
            </h3>
            <div className="font-mono-label text-[10px] text-[#5C6B5E]">
              {sightings.length.toString().padStart(4, "0")} entries
            </div>
          </div>
          <div className="ink-rule mt-5" />

          {loading ? (
            <div className="mt-10 text-[#5C6B5E] font-mono-label text-[11px]">
              Loading the logbook…
            </div>
          ) : sightings.length === 0 ? (
            <div className="mt-16 text-center border border-dashed border-[#D1C9B8] p-12">
              <div className="font-serif-display text-2xl text-[#2A3A2C] italic">
                The logbook is empty.
              </div>
              <p className="text-[#5C6B5E] mt-2 text-sm">
                Be the first to record a sighting.
              </p>
            </div>
          ) : (
            <ul className="mt-6 space-y-6">
              {sightings.map((s, i) => (
                <li
                  key={s.id}
                  data-testid={`sighting-${s.id}`}
                  className="bg-[#FAF8F5] border border-[#D1C9B8] p-6 md:p-7 deckled-edge"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-label text-[10px] text-[#B75D3F]">
                      Entry No. {(sightings.length - i).toString().padStart(4, "0")}
                    </span>
                    <span className="font-mono-label text-[10px] text-[#5C6B5E]">
                      {formatDate(s.sighted_at || s.created_at)}
                    </span>
                  </div>
                  <h4 className="font-serif-display text-2xl text-[#2A3A2C] mt-3 leading-snug">
                    {s.species}
                  </h4>
                  {s.notes && (
                    <p className="text-[#2E362F] mt-3 italic font-serif-display text-lg leading-relaxed">
                      “{s.notes}”
                    </p>
                  )}
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#5C6B5E]">
                    <span className="inline-flex items-center gap-1">
                      <MapPinLine size={14} /> {s.location}
                    </span>
                    <span>—</span>
                    <span>Observer: {s.observer}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] text-[#5C6B5E]">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b border-[#D1C9B8] focus:border-[#2A3A2C] outline-none py-3 text-lg text-[#2A3A2C] placeholder:text-[#8E9B90] transition-colors"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] text-[#5C6B5E]">{label}</span>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-transparent border-b border-[#D1C9B8] focus:border-[#2A3A2C] outline-none py-3 text-lg text-[#2A3A2C] placeholder:text-[#8E9B90] transition-colors resize-none"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="font-mono-label text-[10px] text-[#5C6B5E]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-transparent border-b border-[#D1C9B8] focus:border-[#2A3A2C] outline-none py-3 text-lg text-[#2A3A2C] appearance-none cursor-pointer transition-colors"
      >
        {options.map((o) => (
          <option key={o} value={o} className="text-[#2A3A2C]">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
